import { useState, useRef, useEffect, useCallback } from 'react';
import { Plus, Trash2, Play, Upload } from 'lucide-react';
import type { FieldIssue } from '../../types';
import type { Campaign, ValidationFeedback, AssetOrderToken } from '@/api/campaigns';
import { CharCount, FieldLabel, InlineIssue, Field, inputClass, issueBorder } from './shared';
import { GearExchangeModal, type GXImportedFile } from '../gear-exchange/GearExchangeModal';
import { GearExchangeImportButton } from '../gear-exchange/GearExchangeImportButton';
import { seedSignature } from './seedSignature';
import { revokeCachedBlobUrls } from './revokeCachedBlobUrls';

// ─── Constants ────────────────────────────────────────────────────────────────

const HEADLINE_LIMIT = 34;
const MAX_CARDS = 20;

// ─── Types ────────────────────────────────────────────────────────────────────

interface NewCard {
  id: number;
  kind: 'new';
  file: File;
}
interface ExistingCard {
  id: number;
  kind: 'existing';
  existingAssetId: string;
  previewUrl: string;
  contentType?: string | null;
}
type AnyCard = NewCard | ExistingCard;

let nextId = 1;

// ─── CardThumb sub-component ──────────────────────────────────────────────────

function CardThumb({
  card,
  index,
  isActive,
  isDragging,
  isDragOver,
  onSelect,
  onRemove,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}: {
  card: AnyCard;
  index: number;
  isActive: boolean;
  isDragging: boolean;
  isDragOver: boolean;
  onSelect: () => void;
  onRemove: () => void;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onDragEnd: () => void;
}) {
  // For new (File) cards, create a blob URL with proper lifecycle management
  const [blobSrc, setBlobSrc] = useState<string | null>(null);
  useEffect(() => {
    if (card.kind !== 'new') return;
    const url = URL.createObjectURL(card.file);
    setBlobSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [card]);

  const src = card.kind === 'existing' ? card.previewUrl : blobSrc;
  const isVideo =
    card.kind === 'new'
      ? card.file.type.startsWith('video/')
      : (card.contentType?.startsWith('video/') ?? false);

  if (!src) return null;

  return (
    <div
      draggable
      onClick={onSelect}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      className={`relative flex-shrink-0 h-36 rounded-lg overflow-hidden border-2 cursor-grab active:cursor-grabbing transition-all group/thumb ${
        isDragging ? 'opacity-40' : ''
      } ${
        isDragOver
          ? 'border-slate-400 scale-105'
          : isActive
            ? 'border-[#0014cc]'
            : 'border-slate-200 hover:border-slate-300'
      }`}
      style={{ aspectRatio: '9/16' }}
    >
      {isVideo ? (
        <video
          src={src}
          muted
          playsInline
          preload="auto"
          onLoadedData={(e) => {
            e.currentTarget.currentTime = 0.1;
          }}
          className="w-full h-full object-cover"
        />
      ) : (
        <img src={src} alt={`Card ${index + 1}`} className="w-full h-full object-cover" />
      )}
      {isVideo && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none group-hover/thumb:opacity-0 transition-opacity">
          <div className="w-7 h-7 rounded-full bg-black/50 flex items-center justify-center">
            <Play className="w-3 h-3 text-white fill-white ml-0.5" />
          </div>
        </div>
      )}
      <div className="absolute top-1.5 left-1.5 w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center bg-black/25 text-white">
        {index + 1}
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow opacity-0 group-hover/thumb:opacity-100 transition-opacity"
      >
        <Trash2 className="w-3 h-3" />
      </button>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export interface SnapchatStoryDetailsProps {
  campaignId?: string;
  fordshareIds?: string[] | null;
  campaign?: Campaign | null;
  showErrors: boolean;
  showWarnings: boolean;
  fieldIssues: Record<string, FieldIssue>;
  headline: string;
  onHeadlineChange: (v: string) => void;
  url: string;
  onUrlChange: (v: string) => void;
  legal: string;
  onLegalChange: (v: string) => void;
  activeCardIndex: number;
  onSelectCard: (index: number) => void;
  /** For mockup display: called whenever cards or headline changes */
  onItemsChange: (items: { imageUrl?: string; headline: string }[]) => void;
  /** For save: new files to upload + IDs of existing cards that were deleted */
  onCardsChange: (
    newFiles: File[],
    deletedIds: string[],
    totalCount: number,
    order: AssetOrderToken[],
  ) => void;
  /** Existing cards from DB — pass resolved image URLs (signed or blob proxy) */
  initialItems?: { imageUrl: string; contentType?: string | null; existingAssetId: string }[];
  feedback?: Record<string, ValidationFeedback>;
  feedbackPending?: Set<string>;
  onAIFeedback?: (
    warningKey: string,
    warningMessage: string,
    action: 'accepted' | 'dismissed',
  ) => void;
  usedInVariants?: Map<string, number[]>;
  gxData?: import('@/api/gearExchange').GXMultiSearchResponse | null;
  gxLoading?: boolean;
  gxError?: boolean;
}

export function SnapchatStoryDetails({
  campaignId,
  fordshareIds,
  campaign,
  showErrors,
  showWarnings,
  fieldIssues,
  headline,
  onHeadlineChange,
  url,
  onUrlChange,
  legal,
  onLegalChange,
  activeCardIndex,
  onSelectCard,
  onItemsChange,
  onCardsChange,
  initialItems,
  feedback,
  feedbackPending,
  onAIFeedback,
  usedInVariants,
  gxData,
  gxLoading,
  gxError,
}: SnapchatStoryDetailsProps) {
  const [cards, setCards] = useState<AnyCard[]>([]);
  // Identity of the initial empty array, used to detect the pre-seed render.
  const initialCardsRef = useRef(cards);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const bulkInputRef = useRef<HTMLInputElement>(null);
  const addMoreInputRef = useRef<HTMLInputElement>(null);
  // Accumulates existingAssetIds deleted this session (for server-side deletion on save)
  const deletedIdsRef = useRef<string[]>([]);
  // Signature of the initialItems last seeded from — see seedSignature.ts.
  const seededSigRef = useRef<string | null>(null);
  // Ref for initialItems so notifyParent can check seeding status without
  // depending on prop identity.
  const initialItemsRef = useRef(initialItems);
  initialItemsRef.current = initialItems;
  const blobUrlCacheRef = useRef<Map<number, string>>(new Map());

  // Gear Exchange state
  const [showGxModal, setShowGxModal] = useState(false);
  const [gxInjectedFiles, setGxInjectedFiles] = useState<File[]>();

  // Seed (and re-seed) cards from the DB whenever the incoming asset identities change
  useEffect(() => {
    const sig = seedSignature(initialItems);
    if (!sig || sig === seededSigRef.current) return;
    seededSigRef.current = sig;
    // A re-seed adopts server state wholesale, so pending deletions from the
    // previous seed no longer apply — they are already reflected in initialItems.
    deletedIdsRef.current = [];
    // Previous local previews are dropped wholesale by the re-seed below; their
    // cached blob URLs are no longer reachable for per-card cleanup.
    revokeCachedBlobUrls(blobUrlCacheRef.current);
    setCards(
      initialItems!.map((item) => ({
        id: nextId++,
        kind: 'existing',
        existingAssetId: item.existingAssetId,
        previewUrl: item.imageUrl,
        contentType: item.contentType,
      })),
    );
  }, [initialItems]);

  // Notify parent whenever cards or headline changes
  const notifyParent = useCallback(() => {
    // Mockup items
    onItemsChange(
      cards.map((c) => {
        if (c.kind === 'existing') return { imageUrl: c.previewUrl, headline };
        if (!blobUrlCacheRef.current.has(c.id)) {
          blobUrlCacheRef.current.set(c.id, URL.createObjectURL(c.file));
        }
        return { imageUrl: blobUrlCacheRef.current.get(c.id)!, headline };
      }),
    );
    // Save info
    // Don't push the empty default up to the parent while waiting for DB cards
    // to seed. Identity check rather than the seed signature: on mount the
    // seeding effect runs first and records it before its setCards applies.
    if (initialItemsRef.current?.length && cards === initialCardsRef.current) return;
    const newFiles = cards.filter((c): c is NewCard => c.kind === 'new').map((c) => c.file);
    let newIdx = 0;
    const order: AssetOrderToken[] = cards.map((c) =>
      c.kind === 'existing' ? { existingAssetId: c.existingAssetId } : { newFileIndex: newIdx++ },
    );
    onCardsChange(newFiles, deletedIdsRef.current, cards.length, order);
  }, [cards, headline, onItemsChange, onCardsChange]);

  useEffect(() => {
    notifyParent();
  }, [notifyParent]);

  function addFiles(fileList: FileList | File[]) {
    const files = Array.from(fileList);
    const remaining = MAX_CARDS - cards.length;
    if (remaining <= 0) return;
    setCards((prev) => [
      ...prev,
      ...files.slice(0, remaining).map((file) => ({ id: nextId++, kind: 'new' as const, file })),
    ]);
  }

  useEffect(() => {
    if (gxInjectedFiles && gxInjectedFiles.length > 0) addFiles(gxInjectedFiles);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gxInjectedFiles]);

  function removeCard(card: AnyCard) {
    if (card.kind === 'existing') {
      deletedIdsRef.current = [...deletedIdsRef.current, card.existingAssetId];
    }
    const cached = blobUrlCacheRef.current.get(card.id);
    if (cached) {
      URL.revokeObjectURL(cached);
      blobUrlCacheRef.current.delete(card.id);
    }
    setCards((prev) => prev.filter((c) => c.id !== card.id));
  }

  function handleReorder(fromIndex: number, toIndex: number) {
    if (fromIndex === toIndex) return;
    const next = [...cards];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    setCards(next);
  }

  // ── Drag handlers for card reordering ──────────────────────────────────────

  function onCardDragStart(index: number) {
    setDragIndex(index);
    setIsDraggingFile(false);
  }
  function onCardDragOver(e: React.DragEvent, index: number) {
    e.preventDefault();
    e.stopPropagation();
    if (dragIndex !== null) setDragOverIndex(index);
  }
  function onCardDrop(e: React.DragEvent, index: number) {
    e.preventDefault();
    e.stopPropagation();
    if (dragIndex !== null) handleReorder(dragIndex, index);
    setDragIndex(null);
    setDragOverIndex(null);
  }
  function onCardDragEnd() {
    setDragIndex(null);
    setDragOverIndex(null);
  }

  // ── Drag handlers for file upload zones ────────────────────────────────────

  function onZoneDragOver(e: React.DragEvent) {
    e.preventDefault();
    if (dragIndex === null) setIsDraggingFile(true);
  }
  function onZoneDragLeave() {
    setIsDraggingFile(false);
  }
  function onZoneDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDraggingFile(false);
    if (dragIndex !== null) return; // was a card reorder, not a file drop
    addFiles(e.dataTransfer.files);
  }

  const hasCards = cards.length > 0;
  const atMax = cards.length >= MAX_CARDS;

  return (
    <div className="flex-1 overflow-y-auto hover-scroll px-6 py-5 space-y-6 min-h-0 min-w-0">
      {/* ── About ───────────────────────────────────────────────────── */}
      <div className="border-l-2 border-[#0014cc]/30 pl-3">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
          About this format
        </p>
        <p className="text-sm text-slate-600 leading-relaxed">
          A series of{' '}
          <span className="font-medium text-slate-700">
            up to {MAX_CARDS} full-screen 9:16 cards
          </span>{' '}
          that play as a story sequence on Snapchat. The headline is shared across all cards and
          shown in the footer info box. Users swipe through cards and swipe up to reach the
          destination URL.{' '}
          <a
            href="https://forbusiness.snapchat.com/advertising/ad-formats"
            target="_blank"
            rel="noopener noreferrer"
            className="text-slate-400 hover:text-slate-600 underline underline-offset-2 decoration-slate-300"
          >
            Snapchat ad formats ↗
          </a>
        </p>
      </div>

      {/* ── Gear Exchange Import ──────────────────────────────────── */}
      {
        <>
          <GearExchangeImportButton
            totalAssets={gxData?.total_assets ?? null}
            loading={gxLoading}
            error={gxError}
            fordshareIds={fordshareIds ?? undefined}
            onClick={() => setShowGxModal(true)}
          />

          {showGxModal && gxData && campaign && campaignId && (
            <GearExchangeModal
              data={gxData}
              campaign={campaign}
              campaignId={campaignId}
              formatLabel="Snapchat Story"
              zoneLimits={[{ ratio: '9x16', limit: 20, existing: cards.length }]}
              onClose={() => setShowGxModal(false)}
              usedInVariants={usedInVariants}
              onImport={(files: GXImportedFile[]) => {
                setGxInjectedFiles(files.map((f) => f.file));
              }}
            />
          )}
        </>
      }

      {/* ── Assets ──────────────────────────────────────────────────── */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            9:16 Cards
            <span className="normal-case font-normal tracking-normal ml-1">
              ({cards.length} / {MAX_CARDS})
            </span>
          </p>
          {hasCards && (
            <p className="text-[11px] text-slate-400">Drag to reorder · Click to preview</p>
          )}
        </div>
        <div className="border-b border-slate-200 mb-3" />

        {!hasCards ? (
          /* ── Empty state: bulk upload zone ─────────────────────── */
          <button
            type="button"
            onClick={() => bulkInputRef.current?.click()}
            onDragOver={onZoneDragOver}
            onDragLeave={onZoneDragLeave}
            onDrop={onZoneDrop}
            className={`h-36 w-full rounded-xl border-2 transition-all flex flex-col items-center justify-center gap-2 px-4 ${
              isDraggingFile
                ? 'border-[#0014cc]/50 bg-[#0014cc]/5'
                : fieldIssues['carousel_items']?.errors.length && showErrors
                  ? 'border-red-400 bg-red-50/20 hover:border-red-500'
                  : fieldIssues['carousel_items']?.warnings.length && showWarnings
                    ? 'border-amber-400 bg-amber-50/20 hover:border-amber-500'
                    : 'border-dashed border-slate-200 hover:border-slate-300 hover:bg-slate-50'
            }`}
          >
            <input
              ref={bulkInputRef}
              type="file"
              accept="image/*,video/*"
              multiple
              className="hidden"
              onChange={(e) => {
                if (e.target.files) addFiles(e.target.files);
              }}
            />
            <div
              className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${isDraggingFile ? 'bg-[#0014cc]/10' : 'bg-slate-100'}`}
            >
              <Upload
                className={`w-4 h-4 transition-colors ${isDraggingFile ? 'text-[#0014cc]' : 'text-slate-400'}`}
              />
            </div>
            <div className="text-center">
              <p className="text-xs text-slate-500 font-medium">
                {isDraggingFile ? 'Drop to upload' : 'Click to upload or drag & drop'}
              </p>
              {!isDraggingFile && (
                <p className="text-[11px] text-slate-400 mt-0.5">
                  Select multiple 9:16 files at once
                </p>
              )}
            </div>
          </button>
        ) : (
          /* ── Filled state: horizontal scroll row ───────────────── */
          <div className="overflow-x-auto min-w-0 max-w-full">
            <div className="flex gap-2" style={{ width: 'max-content' }}>
              {cards.map((card, i) => (
                <CardThumb
                  key={card.id}
                  card={card}
                  index={i}
                  isActive={i === activeCardIndex}
                  isDragging={dragIndex === i}
                  isDragOver={dragOverIndex === i && dragIndex !== null && dragIndex !== i}
                  onSelect={() => onSelectCard(i)}
                  onRemove={() => removeCard(card)}
                  onDragStart={() => onCardDragStart(i)}
                  onDragOver={(e) => onCardDragOver(e, i)}
                  onDrop={(e) => onCardDrop(e, i)}
                  onDragEnd={onCardDragEnd}
                />
              ))}

              {/* Add more box */}
              {!atMax && (
                <button
                  type="button"
                  onClick={() => addMoreInputRef.current?.click()}
                  onDragOver={onZoneDragOver}
                  onDragLeave={onZoneDragLeave}
                  onDrop={onZoneDrop}
                  className={`relative flex-shrink-0 h-36 rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-1 transition-all ${
                    isDraggingFile
                      ? 'border-[#0014cc]/50 bg-[#0014cc]/5'
                      : fieldIssues['carousel_items']?.errors.length && showErrors
                        ? 'border-red-400 bg-red-50/20 hover:border-red-500'
                        : fieldIssues['carousel_items']?.warnings.length && showWarnings
                          ? 'border-amber-400 bg-amber-50/20 hover:border-amber-500'
                          : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                  style={{ aspectRatio: '9/16' }}
                  title="Add more cards"
                >
                  <Plus
                    className={`w-5 h-5 transition-colors ${isDraggingFile ? 'text-[#0014cc]' : 'text-slate-400'}`}
                  />
                  <span className="text-[10px] font-medium text-slate-400">Add</span>
                  <input
                    ref={addMoreInputRef}
                    type="file"
                    accept="image/*,video/*"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files) addFiles(e.target.files);
                    }}
                  />
                </button>
              )}
            </div>
          </div>
        )}
        <InlineIssue
          issue={fieldIssues['carousel_items']}
          showErrors={showErrors}
          showWarnings={showWarnings}
          feedback={feedback}
          feedbackPending={feedbackPending}
          onFeedback={onAIFeedback}
        />
      </div>

      {/* ── Shared copy ─────────────────────────────────────────────── */}
      <div>
        <FieldLabel
          label="Headline"
          right={<CharCount value={headline} limit={HEADLINE_LIMIT} />}
          tooltip="Shown in the footer info box on every card. Shared across all cards."
        />
        <input
          type="text"
          value={headline}
          onChange={(e) => onHeadlineChange(e.target.value)}
          placeholder="Short, punchy headline..."
          className={`${inputClass} ${issueBorder(fieldIssues['headline'], showErrors, showWarnings)}`}
        />
        <InlineIssue
          issue={fieldIssues['headline']}
          showErrors={showErrors}
          showWarnings={showWarnings}
          feedback={feedback}
          feedbackPending={feedbackPending}
          onFeedback={onAIFeedback}
        />
      </div>

      {/* ── Destination URL ──────────────────────────────────────────── */}
      <div>
        <FieldLabel
          label="Destination URL"
          tooltip="Where users are taken when they swipe up. Shared across all cards."
        />
        <input
          type="url"
          value={url}
          onChange={(e) => onUrlChange(e.target.value)}
          placeholder="https://www.ford.com/..."
          className={`${inputClass} ${issueBorder(fieldIssues['destination_url'], showErrors, showWarnings)}`}
        />
        <InlineIssue
          issue={fieldIssues['destination_url']}
          showErrors={showErrors}
          showWarnings={showWarnings}
          feedback={feedback}
          feedbackPending={feedbackPending}
          onFeedback={onAIFeedback}
        />
      </div>

      {/* ── Legal ───────────────────────────────────────────────────── */}
      <Field
        label="Legal"
        tooltip="Required in some markets (e.g. France) for regulated categories. Internal only — not displayed in the mockup."
        fieldKey="legal"
        fieldIssues={fieldIssues}
        showErrors={showErrors}
        showWarnings={showWarnings}
        feedback={feedback}
        feedbackPending={feedbackPending}
        onFeedback={onAIFeedback}
        help={<>Enter "N/A" if your market does not require a legal disclaimer.</>}
      >
        <textarea
          value={legal}
          onChange={(e) => onLegalChange(e.target.value)}
          placeholder="Legal disclaimer — required for some markets (e.g. FR)..."
          rows={5}
          className={`${inputClass} resize-none ${issueBorder(fieldIssues['legal'], showErrors, showWarnings)}`}
        />
      </Field>
    </div>
  );
}
